import React, { createContext, useContext, useState, ReactNode, useEffect } from 'react';

interface User {
  _id: string;
  username: string;
  phone: string;
  role: string;
  companyId: string;
  company?: {
    _id: string;
    name: string;
    currency: string;
    signCurrency: string;
    lang: string;
    country: string;
    category?: {
      _id: string;
      name: string;
      nameEnglish: string;
    };
    createdAt?: string;
    updatedAt?: string;
  };
}

interface AuthContextType {
  token: string | null;
  user: User | null;
  setAuthInfo: (token: string, user: User) => void;
  logout: () => void;
  isAuthenticated: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  console.log('AuthProvider rendering');
  
  const [token, setToken] = useState<string | null>(() => {
    const savedToken = localStorage.getItem('token');
    console.log('Initial token from localStorage:', savedToken);
    return savedToken && savedToken !== 'undefined' ? savedToken : null;
  });

  const [user, setUser] = useState<User | null>(() => {
    try {
      const savedUser = localStorage.getItem('user');
      console.log('Initial user from localStorage:', savedUser);
      
      if (!savedUser || savedUser === 'undefined') {
        return null;
      }
      
      const parsedUser = JSON.parse(savedUser);
      console.log('Parsed user:', parsedUser);
      
      // Vérifier que l'objet parsé a la bonne structure
      if (parsedUser && 
          typeof parsedUser === 'object' && 
          '_id' in parsedUser &&
          'username' in parsedUser &&
          'phone' in parsedUser &&
          'role' in parsedUser &&
          'companyId' in parsedUser) {
        return parsedUser;
      }
      
      console.log('Invalid user structure in localStorage');
      return null;
    } catch (error) {
      console.error('Error parsing user data:', error);
      localStorage.removeItem('user'); // Nettoyer les données invalides
      return null;
    }
  });

  useEffect(() => {
    console.log('AuthProvider mounted');
    console.log('Current auth state:', { token, user });
  }, [token, user]);

  const setAuthInfo = (newToken: string, newUser: User) => {
    console.log('Setting auth info:', { newToken, newUser });
    
    if (!newToken || !newUser) {
      console.error('Invalid auth info provided');
      return;
    }
    
    try {
      setToken(newToken);
      setUser(newUser);
      localStorage.setItem('token', newToken);
      localStorage.setItem('user', JSON.stringify(newUser));
      console.log('Auth info successfully saved');
    } catch (error) {
      console.error('Error saving auth info:', error);
    }
  };

  const logout = () => {
    console.log('Logging out');
    try {
      setToken(null);
      setUser(null);
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      console.log('Logout successful');
    } catch (error) {
      console.error('Error during logout:', error);
    }
  };

  const value = {
    token,
    user,
    setAuthInfo,
    logout,
    isAuthenticated: !!token && token !== 'undefined',
  };

  console.log('AuthProvider value:', value);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
